import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getServiceCategoryCarousel, getServicePrice } from 'aster-foundation/core/homecare/category/redux/actions';

import Link from 'next/link';
import { dataLayerSendEvent, getProductItemObject } from 'Epharmacy/EpharmacyAnalytics';
import ServiceItem from '../ServiceItem/ServiceItem';
import SectionBox from '../SectionBox/SectionBox';
import SlickSlider from '../SlickSlider/SlickSlider';
import { CommonServiceCarouselStyled } from './CommonServiceCarouselStyled';
import WithLoader from '../Loader/WithLoader';
import withAnalytics from '../Hoc/WithAnalytics';

import { getProductDetailsAttributes } from '@common/Utility/insider-object-integration';
import {
  errorHandling,
  getPriceValueInServiceData,
  isRxNeeded,
  isServiceAbleToCurrentLocation,
} from '@common/Utility/common-utils';
import {
  HOME_CARE_ROUTE,
  LOCATION_ERROR_MESSAGE,
  PACKAGE_ADDED_IN_CART,
  PACKAGE_ADDED_IN_WISHLIST,
  PACKAGE_REMOVED_FROM_CART,
  PACKAGE_REMOVED_IN_WISHLIST,
} from '@common/Utility/constants';
import { sendEventToSmartLook } from '@common/Utility/smartlook';
import { IC_NO_LOCATION_ICON, IC_PRODUCT_DEFAULT, IC_TICK } from '../../Assets/images/index';
import { sendViewItemListEvent } from 'Homecare/HomecareAnalytics';
import { ToastControl } from '../Forms';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';
import { useTranslation } from '@common/i18n';

export const responsive = [
  {
    breakpoint: 980,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 1,
      variableWidth: true,
      infinite: true,
    },
  },
];

const CommonPackageCarousel = (props) => {
  const { title, carouselTypes, data, brandId, setLoading, viewAllLink, isPackage } = props;

  const dispatch = useDispatch();
  const router = useRouter();
  const generateURL = useNavigationURL();
  const t = useTranslation();
  const offerType = brandId;

  const zoneData = useSelector((state) => state?.app?.userLocationReducer?.zone);
  const loading = useSelector((state) => state?.epharmacy?.getCommonProductCarouselBySearch?.isFetching);
  const guestLogin = useSelector((state) => state?.astercore?.guestAccessToken?.data);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);
  const appConfigData = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const currency = appConfigData?.find((c) => c.name === 'currency')?.value;
  const userLocation = useSelector((state) => state?.app?.userLocationReducer?.location);

  const [serviceCarouselData, setServiceCarouselData] = useState(null);
  const [serviceAddedToastMessage, setServiceAddedToastMessage] = useState(false);
  const [serviceDeletedToastMessage, setServiceDeletedToastMessage] = useState(false);
  const [packageAddedToastMessage, setPackageAddedToastMessage] = useState(false);
  const [packageDeletedToastMessage, setPackageDeletedToastMessage] = useState(false);
  const [serviceSavedToast, setServiceSavedToast] = useState(false);
  const [packageSavedToast, setPackageSavedToast] = useState(false);
  const [serviceRemovedToast, setServiceRemovedToast] = useState(false);
  const [packageRemovedToast, setPackageRemovedToast] = useState(false);
  const [priceArray, setPriceArray] = useState([]);
  const [packageDataWithPrice, setPackageDataWithPrice] = useState([]);
  const [analyticData, setAnalyticData] = useState({});

  const inViewRef = useRef();
  const [eventFired, setEventFired] = useState(false); 

  const zoneDataMemo = React.useMemo(() => zoneData, [zoneData]);

  const carouselTitle = useMemo(() => {
    if (title && title !== '') {
      return title;
    }
    if (serviceCarouselData?.title && serviceCarouselData?.title !== '') {
      return serviceCarouselData?.title;
    }
    if (carouselTypes) {
      return carouselTypes;
    }
    return '';
  }, [title, serviceCarouselData?.title, carouselTypes]);

  const getHrefValue = () => ({
    pathname: generateURL(`${HOME_CARE_ROUTE}/services/package-categories`),
    query: { sourceId: brandId, sourceType: props?.sourceType },
  });

  const rightTopSection = () => {
    if (serviceCarouselData.length > 5) {
      return (
        <Link href={getHrefValue()} passHref>
          <a
            className="link me-2"
            tabIndex="0"
            role="button"
            aria-pressed="false"
            href={generateURL(viewAllLink)}
            onClick={() => {
              dataLayerSendEvent('category_view_all', {
                section_name: props?.title,
              });
            }}
          >
            {t('EPharmacyHome.viewAll')}
          </a>
        </Link>
      );
    }
  };

  const getuserLocationMemo = React.useMemo(() => userLocation, [userLocation?.lat, userLocation?.lng]);

  useEffect(() => {
    if (userLocation) {
      const newArr = [];
      const params = {
        sourceId: brandId,
        sourceType: props?.sourceType,
        latitude: userLocation?.lat,
        longitude: userLocation?.lng,
      };
      dispatch(
        getServiceCategoryCarousel({
          params,
          onSuccess: (response) => {
            setAnalyticData({
              serviceName: props?.title,
              brandId: brandId,
              dataType: 'Package only',
              pageName: props?.pageName,
            });
            response?.response?.forEach((item) => {
              newArr.push(...item.packages);
            });
            setServiceCarouselData(newArr.slice(0, 10));
          },
          onFailure: (err) => {
            errorHandling(err);
          },
        }),
      );
    }
  }, [getuserLocationMemo]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  // useEffect(() => {
  //   if (serviceCarouselData?.data && serviceCarouselData?.data.length > 0) {
  //     dataLayerSendEvent('view_item_list', {
  //       item_list_id: offerType,
  //       item_list_name: carouselTitle,
  //       section_name: carouselTitle,
  //       product_type: 'Epharmacy',
  //       list_type: 'Campaign List',
  //       referrer_page: referrerPage,
  //       ecommerce: {
  //         items: serviceCarouselData?.data.map((element, index) => ({
  //           ...getProductItemObject(element),
  //           index: index + 1,
  //           ...getProductDetailsAttributes(element, 'Pharmacy'),
  //         })),
  //       },
  //     });
  //     const properties = {
  //       type: offerType,
  //     };
  //     sendEventToSmartLook('view_item_list', properties);
  //   }
  // }, [serviceCarouselData?.data]);

  useEffect(() => {
    if (serviceAddedToastMessage) {
      setTimeout(() => {
        setServiceAddedToastMessage(false);
      }, 3000);
    }
    if (serviceDeletedToastMessage) {
      setTimeout(() => {
        setServiceDeletedToastMessage(false);
      }, 3000);
    }
    if (packageAddedToastMessage) {
      setTimeout(() => {
        setPackageAddedToastMessage(false);
      }, 3000);
    }
    if (packageDeletedToastMessage) {
      setTimeout(() => {
        setPackageDeletedToastMessage(false);
      }, 3000);
    }
    if (serviceSavedToast) {
      setTimeout(() => {
        setServiceSavedToast(false);
      }, 3000);
    }
    if (packageSavedToast) {
      setTimeout(() => {
        setPackageSavedToast(false);
      }, 3000);
    }
    if (serviceRemovedToast) {
      setTimeout(() => {
        setServiceRemovedToast(false);
      }, 3000);
    }
    if (packageRemovedToast) {
      setTimeout(() => {
        setPackageRemovedToast(false);
      }, 3000);
    }
  }, [
    serviceAddedToastMessage,
    serviceDeletedToastMessage,
    packageAddedToastMessage,
    packageDeletedToastMessage,
    serviceSavedToast,
    packageSavedToast,
    serviceRemovedToast,
    packageRemovedToast,
  ]);

  useEffect(() => {
    if (serviceCarouselData && userLocation) {
      let hisIDs = [];
      serviceCarouselData?.forEach((item, _index) => {
        hisIDs?.push(item?.hisid);
      });
      if (hisIDs) {
        const params = {
          hisids: hisIDs.join(),
          latitude: userLocation?.lat,
          longitude: userLocation?.lng,
        };
        dispatch(
          getServicePrice({
            params,
            onSuccess: (response) => {
              setPriceArray(response.response?.data);
            },
            onFailure: () => {},
          }),
        );
      }
    }
  }, [serviceCarouselData, userLocation]);

  const handleInView = () => {
    if (!eventFired) {

        let finalResult = getPriceValueInServiceData(serviceCarouselData, priceArray);
        const { serviceName, brandId, dataType, pageName } = analyticData;

        sendViewItemListEvent(finalResult, serviceName, brandId, dataType, pageName);
        setEventFired(true);
    }
  };

  React.useEffect(() => {
    let finalResult;
    if (serviceCarouselData && serviceCarouselData?.length > 0 && priceArray) {
      finalResult = getPriceValueInServiceData(serviceCarouselData, priceArray);
      setPackageDataWithPrice(finalResult);
      if (serviceCarouselData !== null && priceArray?.length > 0 && analyticData) {
      }
    }
  }, [serviceCarouselData, priceArray, analyticData]);

  const showToastControl = (message) => (
    <ToastControl
      className={`${scrollY > 38 ? 'fixed' : ''}`}
      imgBox
      imgError={IC_TICK}
      variant="green-dark-box floating-wrapper toast-image"
      text={message}
    />
  );

  const constructor = handleInView;

  const CommonPackageCarouselWithAnalytics = withAnalytics(() => (

    <CommonServiceCarouselStyled ref={inViewRef}>
      {packageAddedToastMessage && showToastControl(PACKAGE_ADDED_IN_CART)}
      {packageDeletedToastMessage && showToastControl(PACKAGE_REMOVED_FROM_CART)}
      {packageSavedToast && showToastControl(PACKAGE_ADDED_IN_WISHLIST)}
      {packageRemovedToast && showToastControl(PACKAGE_REMOVED_IN_WISHLIST)}
      <SectionBox
        title={carouselTitle}
        customClass="ePharmacy"
        customOuterClass="sectionBox"
        rightTop={rightTopSection()}
      >
        <SlickSlider
          sliderSettings={{
            responsive,
            slidesToShow: 5.35,
            infinite: false,
            className: '',
            dots: false,
          }}
        >
          {packageDataWithPrice?.map((item, index) => (
            <ServiceItem
              className="product-card-wrapper"
              key={item?.id}
              index={index}
              serviceImage={item.photos[0]?.url ? item.photos[0]?.url : IC_PRODUCT_DEFAULT.src}
              name={item?.name}
              deliverBy={item?.serviceTime}
              currency={currency}
              itemAdded={item?.itemAdded !== undefined ? item?.itemAdded : false}
              isService={item?.isService}
              isPackage={item?.dataType === 'Packages' ? 'Packages' : false}
              isRxNeeded={isRxNeeded(item)}
              serviceTime={item?.serviceTime}
              earliestSlot={item?.nextavailableslot}
              price={item?.price}
              availableStock={item.dataType}
              sourceContainerTitle="Search Result"
              isInProductListingPage
              pageName={props?.pageName}
              sectionName={props?.title}
              itemListId={brandId}
              itemListName={props?.title}
              pageType="Package only"
              categoryType="homeCare"
              serviceListQuery={router?.query?.dynamic?.[0]}
              hisId={item?.hisid}
              item={item}
              toastService={(dataType, toastType) => {
                if (dataType === 'SERVICE' && toastType === 'ADDED') {
                  setServiceAddedToastMessage(true);
                } else if (dataType === 'SERVICE' && toastType === 'DELETED') {
                  setServiceDeletedToastMessage(true);
                } else if (dataType === 'PACKAGE' && toastType === 'ADDED') {
                  setPackageAddedToastMessage(true);
                } else if (dataType === 'PACKAGE' && toastType === 'DELETED') {
                  setPackageDeletedToastMessage(true);
                } else if (dataType === 'PACKAGE_WISHLIST' && toastType === 'ADDED') {
                  setPackageSavedToast(true);
                } else if (dataType === 'SERVICE_WISHLIST' && toastType === 'ADDED') {
                  setServiceSavedToast(true);
                } else if (dataType === 'PACKAGE_WISHLIST' && toastType === 'REMOVED') {
                  setPackageRemovedToast(true);
                } else if (dataType === 'SERVICE_WISHLIST' && toastType === 'REMOVED') {
                  setServiceRemovedToast(true);
                }
              }}
            />
          ))}
        </SlickSlider>
      </SectionBox>
    </CommonServiceCarouselStyled>
  ), constructor, inViewRef)

  return packageDataWithPrice && packageDataWithPrice?.length > 0 ? (
    <CommonPackageCarouselWithAnalytics />
  ) : (
    ''
  );
};

CommonPackageCarousel.propTypes = {
  data: PropTypes.object,
  title: PropTypes.string,
  viewAllLink: PropTypes.string,
  brandId: PropTypes.any,
  carouselTypes: PropTypes.string,
  setLoading: PropTypes.func,
  sourceType: PropTypes.string,
  pageName: PropTypes.string,
};

export default WithLoader(CommonPackageCarousel);
